<script setup lang="ts">
import ContextMenu from '@lib/apps/browser/components/ContextMenu/ContextMenu.vue'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import CButton from '@lib/components/CButton/CButton.vue'

import useApiStore, { type CompactFolder } from '@browser/stores/api'
import { ViewCompactItemType, type RenderableFile, type ViewCompactFolder } from '@lib/types/files'
import useAppStore from '@browser/stores/app'
import useThemeVars from '@lib/composables/useThemeVars'
import useAppEmit from '@browser/composables/useAppEmit'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'

import type { ComputedRef } from 'vue'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { state } = useApiStore()
const { selectedFolderId, appName } = useAppStore()
const themeVars = useThemeVars()
const { appEmit } = useAppEmit()
const { createNameDescription, isFolderNameDescriptionEnabled } = useFolderNameDescription()
const fetchedFolder = ref<CompactFolder | null>(null)
const folder = computed(() => {
  if (!fetchedFolder.value) return null

  return {
    ...fetchedFolder.value,
    type: ViewCompactItemType.Folder,
    favoriteId: state.favorites[fetchedFolder.value.id]?.id,
  }
}) as ComputedRef<ViewCompactFolder | null>

const folderNameDescription = createNameDescription(computed(() => folder.value?.name || ''))

const fetchFolder = async () => {
  if (!selectedFolderId.value || !state.folderFetcher) return

  let compactFolder = state.folders.find((f) => f.id === selectedFolderId.value) as unknown as CompactFolder
  // Check also for filtered from Everywhere not to fetch on every click on info button
  if (!compactFolder && state.searchQuery) {
    compactFolder = state.filteredItems.find((f) => f.id === selectedFolderId.value) as unknown as CompactFolder
  }
  if (compactFolder) {
    fetchedFolder.value = compactFolder
  } else {
    const result = await state.folderFetcher?.(selectedFolderId.value)

    if (result) {
      fetchedFolder.value = {
        ...result,
        file_thumbnail_urls: result.files.slice(0, 4).map((file) => file.thumbnail_url),
      } as unknown as CompactFolder
    }
  }
}

// Fetch folder when selectedFolderId changes
watch(selectedFolderId, fetchFolder, { immediate: true })
</script>

<template>
  <div class="basic-info w-full">
    <div class="flex mb-2 basic-info__name">
      <div class="truncate">
        {{ folderNameDescription.name }}
      </div>
    </div>
    <div
      v-if="isFolderNameDescriptionEnabled && folderNameDescription.description"
      class="font-size-[14px] tracking-[0.4px] leading-normal color-text2 whitespace-pre-wrap mb-2"
    >
      {{ folderNameDescription.description }}
    </div>

    <CButton
      v-if="appName === 'admin'"
      class="w-full mt-4 mb-6"
      @click="appEmit('viewFolderDetails', selectedFolderId)"
    >
      {{ t('canvasUI.browserApp.folderSelectionInfo.sections.basic.details') }}
    </CButton>
    <div v-if="folder" class="relative w-full">
      <CFileThumbnail :height="200" :item="folder as RenderableFile" width="100%" />
      <div class="absolute top-2 right-2">
        <ContextMenu v-if="folder" :item="folder" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.basic-info {
  &__name {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.5px;
  }
  &__icon,
  &__image {
    border-radius: 4px;
    border: 1px solid v-bind('themeVars.primary6');
    background: v-bind('themeVars.primary6_1');
  }
  &__icon {
    padding: 16px;
  }
  &__icon__element {
    color: v-bind('themeVars.primary5');
  }
}
</style>
